/**
 * Tenant discovery. The workbench takes the domain of the signed-in user's
 * email (`jean-pierre.simonis@mojoup.com.au` -> `mojoup.com.au`), resolves
 * `aioe-discover.<domain>` (a CNAME to the tenant's endpoint, or a TXT
 * record carrying `v=aioe1; url=https://aioe.mojoup.com.au`), and then
 * fetches `https://<endpoint>/.well-known/aioe.json`, which is this document.
 *
 * The document is public and carries no secrets: it tells a client where to
 * authenticate and which capabilities this deployment offers.
 */
import { z } from "zod";
export const DISCOVERY_RECORD_PREFIX = "aioe-discover";
export const DISCOVERY_WELL_KNOWN_PATH = "/.well-known/aioe.json";
export const DISCOVERY_TXT_VERSION = "aioe1";
export const discoveryDocumentSchema = z.object({
    version: z.literal(1),
    /** Tenant slug as known to this deployment (one deployment serves many tenants). */
    tenant: z.string().min(1).max(100),
    /** Display name for the sign-in screen. */
    organisation: z.string().min(1).max(200),
    /** Base URL of the AIOE API for this tenant (may be shared across tenants). */
    apiUrl: z.string().url(),
    /** Base URL the workbench relay client dials (device-code, stream, respond, events). */
    relayUrl: z.string().url(),
    /** Base URL of the coordination server for the WireGuard overlay. */
    meshUrl: z.string().url().optional(),
    /** Keys the platform signs mesh-borne remote-control requests with (see controlSignature.ts). */
    signingKeysUrl: z.string().url().optional(),
    auth: z.object({
        kind: z.literal("oidc"),
        /** OpenID Connect issuer, e.g. https://login.microsoftonline.com/<tenantId>/v2.0 */
        issuer: z.string().url(),
        /** Public client id the workbench uses for the device-code / PKCE flow. */
        clientId: z.string().min(1),
        /** Scope the workbench must request to call the AIOE API. */
        apiScope: z.string().min(1),
        deviceAuthorizationEndpoint: z.string().url().optional(),
    }),
    capabilities: z.array(z.enum(["ingest", "catalogue", "policy", "relay", "mesh", "remote-control"])),
    /**
     * DPoP (RFC 9449) is accepted for device tokens, with these proof algorithms (the OAuth metadata's
     * `dpop_signing_alg_values_supported`). A field and not a capability: released desktops read
     * capabilities as a closed list and ignore fields they do not know.
     */
    dpopSigningAlgs: z.array(z.enum(["ES256", "EdDSA"])).optional(),
    /**
     * Which edition answers: `enterprise` (self-hosted AIOE, one organisation per issuer) or `cloud`
     * (Mojo Up AI Cloud: many organisations, chosen when the device is approved). Absent = enterprise.
     * A field and not a capability, for the same reason as `dpopSigningAlgs`.
     */
    edition: z.enum(["enterprise", "cloud"]).optional(),
    /**
     * Where this platform serves releases (`/releases/v1`, releases.ts): the update feed a workbench
     * or node follows when `updates.source` is empty and this platform governs it. A field, for the
     * same reason as `edition`.
     */
    releasesUrl: z.string().url().optional(),
    /**
     * How a workbench signs a person in to Entra-protected tool servers (Microsoft 365 through Work
     * IQ): the public client registered for a desktop's loopback sign-in (`http://127.0.0.1`), at
     * `authority`, else at `tenantId`, else at the tenant of `auth.issuer`. No scope: the tool
     * server's own discovery names it. Never the console's `auth.clientId`, and never the desktop's
     * own sign-in (that is the workbench's reserved `entra` block). One that does not parse reads as
     * absent. Mirrors the workbench's src/platform/discovery.ts (7a974a9e).
     */
    /**
     * Mojo Up AI Cloud: Cloudflare Turnstile's site key when sign-up checks are on. The console then
     * shows the widget on email sign-in and on creating a team, and sends its answer as
     * `turnstileToken`. Absent = no check. Cloud only; a workbench ignores it.
     */
    turnstileSiteKey: z.string().min(1).max(200).optional(),
    /**
     * Mojo Up AI Cloud: the Google Analytics measurement id when usage statistics are on. The console
     * asks each person first and loads nothing until they agree; the phone app reads it as "this
     * platform counts usage" and asks the same. Absent = none, always on Enterprise. A workbench ignores it.
     */
    analyticsId: z.string().regex(/^G-[A-Z0-9]{4,20}$/).optional(),
    microsoftSignIn: z
        .object({
        clientId: z.string().min(1).max(100),
        tenantId: z.string().min(1).max(100).optional(),
        authority: z.string().url().startsWith("https://").optional(),
    })
        .optional()
        .catch(undefined),
});
/**
 * Parse an `aioe-discover` TXT record. The spec form is `v=aioe1; url=https://host`;
 * a bare hostname or https URL is accepted too, since that is what people type
 * into a DNS console first. Returns undefined when the record is not ours.
 */
export function parseDiscoveryTxt(record) {
    const text = record.trim().replace(/^"|"$/g, "");
    if (/^https:\/\/[^\s;]+$/i.test(text))
        return { url: text.replace(/\/+$/, "") };
    if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(text))
        return { url: `https://${text.toLowerCase()}` };
    const fields = new Map();
    for (const part of text.split(";")) {
        const [k, ...rest] = part.trim().split("=");
        if (k && rest.length)
            fields.set(k.trim().toLowerCase(), rest.join("=").trim());
    }
    if (fields.get("v") !== DISCOVERY_TXT_VERSION)
        return undefined;
    const url = fields.get("url");
    if (!url || !/^https:\/\//i.test(url))
        return undefined;
    return { url: url.replace(/\/+$/, "") };
}
/** The DNS name a client queries for a given email domain. */
export function discoveryHostFor(emailDomain) {
    const domain = emailDomain.trim().toLowerCase().replace(/^.*@/, "");
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain))
        throw new Error(`not a domain: ${emailDomain}`);
    return `${DISCOVERY_RECORD_PREFIX}.${domain}`;
}
//# sourceMappingURL=discovery.js.map